import React from "react";
import { Button, Chip, Select } from "@legal-500/legal500-design-system";
import { SearchableSelect } from "../Form/SearchableSelect/SearchableSelect";
import { MultiSelectFilter } from "./MultiSelectFilter";
import styles from "./DataFilters.module.css";

export type FilterConfig = {
  key: string;
  label: string;
  options: { value: string | number; label: string }[];
  disabled?: boolean;
  searchable?: boolean;
  /**
   * Checkbox multi-select. `options[0]` is the "All …" sentinel (as for the other
   * variants) and gives the trigger text; the rest are the selectable values.
   * `values[key]` is the CSV of selected values, or "all" / "" when none.
   */
  multiple?: boolean;
  /**
   * A ready-made control (e.g. an Address Book typeahead) rendered in this
   * filter's slot, in order with the others, instead of a dropdown built from
   * `options`. The control owns its own label and value; `options` may be [].
   */
  control?: React.ReactNode;
  labelExtra?: React.ReactNode;
  title?: string;
};

/** A `multiple` filter's CSV from `values`, as a list; missing, "" and "all" all mean none. */
function toMultiSelectValue(value: string | number | undefined): string[] {
  if (value === undefined || value === "" || value === "all") return [];
  return String(value).split(",").filter(Boolean);
}

export type ActiveFilter = {
  key: string;
  label: string;
  value: string | number;
  displayValue?: string;
};

type DataFiltersProps = {
  filters: FilterConfig[];
  values: Record<string, string | number>;
  onChange: (key: string, value: string | number) => void;
  activeFilters?: ActiveFilter[];
  onClearFilter?: (key: string) => void;
  onClearAll?: () => void;
  children?: React.ReactNode;
  /**
   * Optional custom content rendered as the FIRST field in the dropdown row,
   * as a peer of the filter dropdowns (e.g. an inline free-text search field).
   */
  leading?: React.ReactNode;
  /**
   * "flow" (the default): controls grow to fill the row, as every four-or-so-filter page
   * (Submissions, Surveys, Document Parsing) already does. "grid": equal-width cells instead, for
   * a row with enough filters — Awards' ten — that a flex row would wrap unevenly and stretch a
   * lone last control to fill the line.
   */
  layout?: "flow" | "grid";
};

export function DataFilters({
  filters,
  values,
  onChange,
  activeFilters = [],
  onClearFilter,
  onClearAll,
  children,
  leading,
  layout = "flow",
}: DataFiltersProps) {
  return (
    <>
      {(leading || filters.length > 0) && (
        <div className={`${styles.filtersContent} ${layout === "grid" ? styles.filtersGrid : ""}`}>
          {leading}
          {filters.map(filter => (
            <div
              key={filter.key}
              className={`${styles.filterDropdown} ${
                filter.key === "edition" ? styles.filterDropdownSmall : ""
              }`}
              title={filter.title}
            >
              {filter.labelExtra && (
                <div className={styles.filterLabelRow}>
                  <span className={styles.filterLabelText}>{filter.label}</span>
                  {filter.labelExtra}
                </div>
              )}
              {filter.control ? (
                filter.control
              ) : filter.multiple ? (
                <MultiSelectFilter
                  name={filter.key}
                  label={filter.labelExtra ? "" : filter.label}
                  allLabel={filter.options[0]?.label ?? "All"}
                  options={filter.options
                    .slice(1)
                    .map(option => ({ value: String(option.value), label: option.label }))}
                  value={toMultiSelectValue(values[filter.key])}
                  onChange={next => onChange(filter.key, next.join(",") || "all")}
                  disabled={filter.disabled}
                />
              ) : filter.searchable ? (
                <SearchableSelect
                  name={filter.key}
                  label={filter.labelExtra ? "" : filter.label}
                  value={values[filter.key] === "all" ? null : (values[filter.key] ?? null)}
                  options={filter.options.slice(1)}
                  onChange={val => onChange(filter.key, val ?? "all")}
                  disabled={filter.disabled}
                  placeholder={filter.options[0]?.label}
                />
              ) : (
                <Select
                  label={filter.labelExtra ? "" : filter.label}
                  value={values[filter.key]}
                  onChange={e => onChange(filter.key, e.target.value)}
                  options={filter.options}
                  size="small"
                  disabled={filter.disabled}
                />
              )}
              {filter.disabled && filter.title && (
                <span className={styles.filterHint}>{filter.title}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.filtersAndPaginationRow}>
        {activeFilters.length > 0 && onClearFilter && (
          <div className={styles.activeFilters}>
            <span className={styles.activeFiltersLabel}>Active filters:</span>
            <div className={styles.activeFiltersChips}>
              {activeFilters.map(filter => (
                <Chip
                  key={filter.key}
                  size="small"
                  variant="outlined"
                  onDelete={() => onClearFilter(filter.key)}
                  className={styles.filterChip}
                >
                  {filter.label}: {filter.displayValue || filter.value}
                </Chip>
              ))}
            </div>
            {onClearAll && activeFilters.length >= 2 && (
              <Button
                size="small"
                variant="text"
                color="primary"
                onClick={onClearAll}
                className={styles.clearAllButton}
              >
                Clear all
              </Button>
            )}
          </div>
        )}
        {children}
      </div>
    </>
  );
}
